import React from "react";
import styled, { css } from "styled-components";

import Graph from "src/library/components/Graph";
import {
  TotalTicketsResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupTotalTickets } from "./utils";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { NumberWidget } from "../NumberWidget";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface TotalTicketsGraphValue extends Omit<GraphPayload, "date"> {
  date: Date;
  count: number;
}

const SUPPORT_TOTAL_TICKETS_CACHE_KEY = "widget_support_total_tickets";

/**
 * Returns support total tickets widget content
 */
export const TotalTicketsWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, widgetTypeId, ignoreCache = false
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ totalTicketsResponse, totalTicketsRequest ] = useWidgetDataApi("GET_TOTAL_TICKETS");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  // On load, get the data we need
  React.useEffect(() => {
    totalTicketsRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => `${SUPPORT_TOTAL_TICKETS_CACHE_KEY}-${widgetTypeId}`,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    totalTicketsRequest,
    widgetTypeId
  ]);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange & xAxisBy state
  const transformToGraphData = React.useCallback((totalTicketsResult: TotalTicketsResult): TotalTicketsGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(totalTicketsResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);

    // Group, accumulate totals and return
    return groupTotalTickets(totalTicketsResult, calculatedDisplayRange);
  }, [ ]);

  // The data used to graph view widget
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (totalTicketsResponse.data?.result) {
        return transformToGraphData(totalTicketsResponse.data?.result);
      }
    }

    return null;
  }, [
    totalTicketsResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ "date" ]) : [];
    }

    return null;
  }, [ graphDisplayData, selectedView ]);

  // The data used for number view widget
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const totalTicketsResult = totalTicketsResponse.data?.result;

      if (totalTicketsResult) {
        return { value: totalTicketsResult.reduce((total, totalTickets) => total + totalTickets.count, 0) };
      }
    }

    return null;
  }, [ totalTicketsResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line and number are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {totalTicketsResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !totalTicketsResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader title="Total Tickets" />

          <Graph
            containerWidth={width && width}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType="number"
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: "date",
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !totalTicketsResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          units="Total Tickets"
          valueType="number"
          fontSize={fontSize}
          hideToolTip="header"
        />
      )}

      {totalTicketsResponse.error && (
        <DataLoadError errorMessage={totalTicketsResponse.error.message} />
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
